import React from "react";
import { STATUS_TIMELINE, timelineIndexFor } from "@/lib/api";
import { Check } from "lucide-react";

/**
 * Customer-facing visual timeline for a project's lifecycle.
 *
 * Shows 6 steps: Submitted → Under review → Quote sent → Approved → In progress → Handover.
 * Steps before `currentIdx` are filled; the current step is highlighted in orange.
 */
export default function StatusTimeline({ status, dense = false }) {
  const currentIdx = timelineIndexFor(status);
  const rejected = status === "rejected";
  const moreInfo = status === "awaiting_customer_info";

  return (
    <div
      className={`w-full ${dense ? "py-2" : "py-3"}`}
      data-testid="status-timeline"
    >
      <div className="flex items-center justify-between gap-1 sm:gap-2">
        {STATUS_TIMELINE.map((step, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx && !rejected;
          const dimmed = i > currentIdx || rejected;
          let bg = "var(--bq-border)";
          let color = "var(--bq-text-muted)";
          if (done) {
            bg = "var(--bq-green)";
            color = "#fff";
          } else if (active) {
            bg = "var(--bq-orange)";
            color = "#fff";
          }
          return (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center text-center flex-1 min-w-0">
                <div
                  className="rounded-full flex items-center justify-center font-bold text-xs"
                  style={{
                    width: dense ? 22 : 28,
                    height: dense ? 22 : 28,
                    background: bg,
                    color,
                    opacity: dimmed && !active ? 0.5 : 1,
                    transition: "all 200ms ease",
                  }}
                >
                  {done ? <Check size={dense ? 12 : 14} /> : i + 1}
                </div>
                <span
                  className="overline mt-1 truncate w-full"
                  style={{
                    color: active ? "var(--bq-orange)" : "var(--bq-text-muted)",
                    fontSize: dense ? 9 : 10,
                  }}
                >
                  {step.label}
                </span>
              </div>
              {i < STATUS_TIMELINE.length - 1 && (
                <div
                  className="flex-1 h-0.5 mb-4"
                  style={{
                    background:
                      i < currentIdx ? "var(--bq-green)" : "var(--bq-border)",
                    opacity: rejected ? 0.3 : 1,
                    transition: "background 200ms ease",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {rejected && (
        <div
          className="mt-2 text-center text-xs font-semibold"
          style={{ color: "var(--bq-red)" }}
        >
          Request declined
        </div>
      )}
      {moreInfo && (
        <div
          className="mt-2 text-center text-xs font-semibold"
          style={{ color: "var(--bq-amber)" }}
        >
          We've asked for more information — please check below
        </div>
      )}
    </div>
  );
}
